import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Heart, Home, UserCheck, Baby, MapPin } from 'lucide-react';

const FamilyDependentVisas = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Family and Dependent Visas
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Reunite and stay together with a clear plan and compliant evidence.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We plan and prepare applications for partners, children, parents in limited categories, and other dependent family members where the rules allow. We regularly handle the United Kingdom, United States, Canada, Germany and wider EU, Australia, and the UAE. Response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk for evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who This Is For */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Who this is for</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Spouse, partner, or fiancé applicants joining a citizen, resident, or eligible worker or student",
              "Children joining one or both parents, including newborns and adopted children", 
              "Parents or other relatives in limited dependency routes where available",
              "Families joining a principal applicant who holds or is applying for a work or study visa",
              "Applicants with a previous refusal who need a stronger relationship and financial evidence strategy"
            ].map((item, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-6 text-center">
                <Heart className="w-8 h-8 text-black mx-auto mb-4" />
                <p className="text-gray-600 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Outcomes */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Outcomes you can expect</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: "Written Route Plan",
                description: "A written route plan with fees, timelines, and evidence lists for each family member"
              },
              {
                icon: Heart,
                title: "Relationship Narrative",
                description: "A credible relationship narrative that matches independent documents and timelines"
              },
              {
                icon: Calendar,
                title: "Correct Sequencing",
                description: "Correct sequencing of principal and dependents to avoid timing conflicts"
              },
              {
                icon: CheckCircle,
                title: "Complete Application",
                description: "A complete, quality-checked application file and booked appointments"
              },
              {
                icon: MapPin,
                title: "Post-Decision Guidance",
                description: "Post-decision guidance on travel, registrations, and renewal or settlement planning"
              }
            ].map((outcome, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <outcome.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-semibold text-acBlack mb-3">{outcome.title}</h3>
                <p className="text-gray-600">{outcome.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Eligibility Basics */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Eligibility basics</h2>
          <div className="space-y-6">
            {[
              "Genuine relationship that can be supported with documents and history",
              "Sponsor status that qualifies for family sponsorship or for dependents of a worker or student",
              "Financial requirement met through income, savings, or permitted combinations where applicable",
              "Suitable accommodation and care arrangements",
              "Health and character requirements satisfied"
            ].map((requirement, index) => (
              <div key={index} className="flex items-start">
                <CheckCircle className="w-6 h-6 text-blue-600 mr-4 mt-1 flex-shrink-0" strokeWidth={2.5} />
                <p className="text-gray-600 text-lg">{requirement}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Typical Routes */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-acBlack mb-8 text-center">Typical routes we cover</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                country: "United Kingdom",
                routes: "Partner, fiancé, child and dependent child, dependents of workers and students, certain family reunion routes"
              },
              {
                country: "United States", 
                routes: "Spouse and immediate relative categories, K-1 fiancé, dependents of temporary workers and students"
              },
              {
                country: "Canada",
                routes: "Spousal and common-law sponsorship, dependents of workers and students"
              },
              {
                country: "Germany and EU",
                routes: "Family reunification for spouses and minor children, dependents of EU Blue Card and national permit holders"
              },
              {
                country: "Australia",
                routes: "Partner and Prospective Marriage, dependents of TSS or ENS workers and students"
              },
              {
                country: "UAE",
                routes: "Family sponsorship for spouse, children, and in limited cases parents"
              }
            ].map((route, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-semibold text-acBlack mb-3">{route.country}</h3>
                <p className="text-gray-600">{route.routes}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600 italic">
              If your route is not listed, ask during intake. We can coordinate additional destinations through partners.
            </p>
          </div>
        </div>
      </motion.section>


    </div>
  );
};

export default FamilyDependentVisas;
